import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'
import {
  clientLibraryImagesUpdate,
  clientLibraryUpdate,
} from '@disco/state-management/actions/client-library-list-actions'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import LibraryHeader from 'components/common/library-header'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import InlineEdit from 'components/ui/inline-edit'
import PageContainer from 'components/ui/page-container'
import { useFormState } from 'hooks/use-form-state'
import styles from './index.module.css'

type FormFields = 'display_name' | 'description'
type ImageFields = 'image_banner_url' | 'image_logo_url'
const FORM_FIELDS: FormFields[] = ['display_name', 'description']
const IMAGE_FIELDS: ImageFields[] = ['image_banner_url', 'image_logo_url']

const LibrariesLibraryDesign = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const dispatch = useDispatch()
  const library: ClientLibrary = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const [formState, setFormState, pendingChanges] = useFormState(
    library,
    FORM_FIELDS,
  )
  const [images, setImages] = useFormState(library, IMAGE_FIELDS)
  const [imagesChanged, setImagesChanged] = useState(false)

  const uploadImages = (
    name: string,
    file: File,
    onProgress?: (percent: number) => unknown,
  ) => {
    setImagesChanged(true)
    dispatch(
      clientLibraryImagesUpdate(
        libraryId,
        {
          [name]: file,
        },
        onProgress,
      ),
    )
  }

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(clientLibraryUpdate(libraryId, formState))
  }

  return (
    <PageContainer>
      <PageContainer.Section title="Catalog design">
        {library && !library['loading'] && (
          <DocumentHeadTags
            title={`${library['name']} | Catalog design | DISCO`}
          />
        )}
        <Row columnsSize={[12]}>
          <div>
            <LibraryHeader
              title={
                <>
                  <InlineEdit
                    name="display_name"
                    placeholder="Enter name"
                    value={formState['display_name']}
                    onChange={setFormState}
                  />
                  <span className={styles.libraryDesignButtonWrapper}>
                    <Button
                      appearance="icon"
                      icon="info-outline"
                      tooltip="This title will appear on the home page of your Catalog. It will also show for supervisors if you opt in to make your Catalog discoverable, so make sure it clearly identifies your company or artist name."
                    />
                  </span>
                </>
              }
              description={
                <InlineEdit
                  name="description"
                  placeholder="Enter description"
                  value={formState['description']}
                  onChange={setFormState}
                />
              }
              images={images}
              onImagesChange={uploadImages}
            />
          </div>
        </Row>
        <PageContainer.Footer inline>
          <ButtonContainer>
            <Button
              disabled={
                !library ||
                library.loading ||
                (!pendingChanges && !imagesChanged)
              }
              appearance="primary"
              caption="Save"
              onClick={handleSubmit}
            />
          </ButtonContainer>
        </PageContainer.Footer>
      </PageContainer.Section>
    </PageContainer>
  )
}

export default LibrariesLibraryDesign
